/**
 * Canonical Schema.org entity graph for owlscran.com.
 *
 * Every page must reference these `@id` values rather than redeclaring the
 * nodes. A JSON-LD `@id` is a URI: if two pages define the same `@id` with
 * different property values, a consumer building a knowledge graph across the
 * site receives contradictory facts for one entity and cannot resolve which is
 * authoritative. Equally, referencing an `@id` that is never defined in the
 * same document leaves a dangling pointer — `@id` references only resolve
 * within a single document, so a page that points at `#website` without
 * emitting the WebSite node carries no publisher signal at all.
 *
 * Rule: declare a node once per document, reference it everywhere else.
 */

export const SITE_URL = 'https://www.owlscran.com';

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const SOFTWARE_ID = `${SITE_URL}/#software`;
/** Declared by /link-in-bio; pages about the link in bio reference it as their subject. */
export const LINK_IN_BIO_SOFTWARE_ID = `${SITE_URL}/link-in-bio#software`;

/**
 * Google's Organization.logo guidance requires a raster format; an SVG is
 * silently dropped. android-chrome-512x512.png is a genuine 512x512 raster.
 */
export const LOGO_URL = `${SITE_URL}/android-chrome-512x512.png`;
export const LOGO_SIZE = 512;

export const OG_IMAGE_URL = `${SITE_URL}/og-image.png`;
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

export const SCHEMA_CONTEXT = 'https://schema.org';

const personId = (slug: string) => `${SITE_URL}/#person-${slug}`;

export const OLI_ID = personId('oli-paterson');
export const MATT_ID = personId('matt-buckley');

/**
 * Founders as first-class Person entities rather than bare name stubs.
 * Named humans with verifiable experience are a materially stronger E-E-A-T
 * signal than an organisation-only byline.
 */
export const founderNodes = [
  {
    '@type': 'Person',
    '@id': OLI_ID,
    name: 'Oli Paterson',
    jobTitle: 'Co-Founder',
    url: `${SITE_URL}/#founders`,
    image: `${SITE_URL}/oli.png`,
    description:
      'Creator with over 3.5 million followers. Oli works hands-on with creators and brands, and understands what brands look for when they evaluate a partnership.',
    worksFor: { '@id': ORGANIZATION_ID },
    knowsAbout: [
      'Influencer marketing',
      'Brand partnerships',
      'Creator media kits',
      'Sponsorship pricing',
    ],
  },
  {
    '@type': 'Person',
    '@id': MATT_ID,
    name: 'Matt Buckley',
    jobTitle: 'Co-Founder',
    url: `${SITE_URL}/#founders`,
    image: `${SITE_URL}/matt.jpeg`,
    description:
      'Over 15 years building in tech, from investment banks and fintech to nightlife startups. Matt builds tools that are simple, fair, and genuinely useful.',
    worksFor: { '@id': ORGANIZATION_ID },
    knowsAbout: ['Creator tools', 'Social media analytics', 'Software engineering'],
  },
] as const;

const organizationNode = {
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: 'OwlScran',
  legalName: 'OwlScran Ltd',
  url: SITE_URL,
  description:
    'OwlScran helps influencers and content creators get paid fairly by making it easy to create and share professional media kits with brands.',
  logo: {
    '@type': 'ImageObject',
    url: LOGO_URL,
    width: LOGO_SIZE,
    height: LOGO_SIZE,
  },
  image: LOGO_URL,
  // Verify each URL resolves before adding. A sameAs pointing at a profile
  // that does not exist weakens entity resolution rather than helping it.
  sameAs: [
    'https://x.com/owlscran',
    'https://www.instagram.com/owlscran',
    'https://www.tiktok.com/@owlscran',
    'https://www.youtube.com/@owlscran',
  ],
  email: 'contact@owlscran.com',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'contact@owlscran.com',
    contactType: 'customer service',
  },
  foundingDate: '2024',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'GB',
  },
  identifier: {
    '@type': 'PropertyValue',
    propertyID: 'Companies House',
    value: '15305650',
  },
  founder: founderNodes.map((person) => ({ '@id': person['@id'] })),
};

const websiteNode = {
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: SITE_URL,
  name: 'OwlScran - Free Media Kit Generator for Creators',
  description:
    'OwlScran builds free media kits from connected Instagram, TikTok and YouTube accounts, and Facebook Pages. Creators choose which stats, content and rates to share.',
  inLanguage: 'en-GB',
  publisher: { '@id': ORGANIZATION_ID },
};

/**
 * The canonical product node. Platform landing pages describe a *variant* of
 * this product, so they must mint their own `@id` and point back here with
 * `isPartOf` — never redeclare `SOFTWARE_ID` with different values.
 */
export const softwareNode = {
  '@type': 'WebApplication',
  '@id': SOFTWARE_ID,
  name: 'OwlScran Media Kit Generator',
  url: SITE_URL,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description:
    'Free media kit generator for influencers and content creators. Connect Instagram, TikTok or YouTube accounts, or a Facebook Page. OwlScran builds a media kit from your connected accounts; review the details and create a share link for brands.',
  publisher: { '@id': ORGANIZATION_ID },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'GBP',
    availability: 'https://schema.org/InStock',
  },
  featureList: [
    'Auto-sync Instagram stats',
    'Auto-sync TikTok stats',
    'Auto-sync YouTube stats',
    'Connect Facebook Pages',
    'Automatic media kit creation',
    'Accent colour customisation',
    'Share links with selected data',
    'Privacy controls',
    'No design skills required',
  ],
};

/**
 * Organization + WebSite + founders. Spread into the `@graph` of every public
 * page so that `@id` references to the organisation and site actually resolve.
 */
export const sharedGraph = [organizationNode, websiteNode, ...founderNodes];

/** Organization + WebSite + founders + the canonical product node. */
export const sharedGraphWithSoftware = [...sharedGraph, softwareNode];

type FaqQuestion = {
  question: string;
  answer: string;
  answerLink?: { anchor: string; href: string };
};

function absoluteSiteUrl(href: string): string {
  if (href.startsWith('https://') || href.startsWith('http://')) return href;
  const path = href.startsWith('/') ? href : `/${href}`;
  return `${SITE_URL}${path}`;
}

function faqAnswerForSchema(question: FaqQuestion): string {
  const href = question.answerLink ? absoluteSiteUrl(question.answerLink.href) : null;
  if (!href || question.answer.includes(href)) return question.answer;
  return `${question.answer} ${href}`;
}

export function faqPageNode(pageUrl: string, questions: readonly FaqQuestion[]) {
  return {
    '@type': 'FAQPage',
    '@id': `${pageUrl}#faq`,
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faqAnswerForSchema(q),
      },
    })),
  };
}

export function breadcrumbNode(pageUrl: string, label: string) {
  return {
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: label, item: pageUrl },
    ],
  };
}

type BreadcrumbItem = { name: string; item: string };

export function breadcrumbTrailNode(pageUrl: string, items: readonly BreadcrumbItem[]) {
  return {
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      ...items.map((entry, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: entry.name,
        item: entry.item,
      })),
    ],
  };
}

type WebPageOptions = {
  pageUrl: string;
  name: string;
  description: string;
  /** `@id` of the primary entity this page is about. Defaults to the product. */
  aboutId?: string;
  /** Omit for pages with no breadcrumb node in the same graph. */
  hasBreadcrumb?: boolean;
  /** Set false for intentionally text-only pages with no representative image. */
  includePrimaryImage?: boolean;
  /** ISO date the page's editorial claims were last checked. */
  dateModified?: string;
};

export function webPageNode({
  pageUrl,
  name,
  description,
  aboutId = SOFTWARE_ID,
  hasBreadcrumb = true,
  includePrimaryImage = true,
  dateModified,
}: WebPageOptions) {
  return {
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name,
    description,
    inLanguage: 'en-GB',
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': aboutId },
    ...(dateModified ? { dateModified } : {}),
    ...(includePrimaryImage
      ? {
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: OG_IMAGE_URL,
            width: OG_IMAGE_WIDTH,
            height: OG_IMAGE_HEIGHT,
          },
        }
      : {}),
    ...(hasBreadcrumb ? { breadcrumb: { '@id': `${pageUrl}#breadcrumb` } } : {}),
  };
}

type ArticleOptions = {
  pageUrl: string;
  headline: string;
  description: string;
  datePublished?: string;
  dateModified: string;
  /** `@id` of the authoring Person. Defaults to the organisation. */
  authorId?: string;
};

export function articleNode({
  pageUrl,
  headline,
  description,
  datePublished,
  dateModified,
  authorId = ORGANIZATION_ID,
}: ArticleOptions) {
  return {
    '@type': 'Article',
    '@id': `${pageUrl}#article`,
    headline,
    description,
    url: pageUrl,
    ...(datePublished ? { datePublished } : {}),
    dateModified,
    inLanguage: 'en-GB',
    image: {
      '@type': 'ImageObject',
      url: OG_IMAGE_URL,
      width: OG_IMAGE_WIDTH,
      height: OG_IMAGE_HEIGHT,
    },
    isPartOf: { '@id': WEBSITE_ID },
    mainEntityOfPage: { '@id': `${pageUrl}#webpage` },
    publisher: { '@id': ORGANIZATION_ID },
    author: { '@id': authorId },
    about: { '@id': SOFTWARE_ID },
  };
}

type BlogPostingOptions = {
  pageUrl: string;
  headline: string;
  description: string;
  datePublished: string;
  dateModified: string;
  /** `@id` of the authoring Person. Organisation bylines are not allowed. */
  authorId: string;
  articleSection: string;
  wordCount: number;
};

/**
 * Blog-specific article markup. Cover images are intentionally optional in
 * the publishing model, so this node does not invent or require one.
 */
export function blogPostingNode({
  pageUrl,
  headline,
  description,
  datePublished,
  dateModified,
  authorId,
  articleSection,
  wordCount,
}: BlogPostingOptions) {
  return {
    '@type': 'BlogPosting',
    '@id': `${pageUrl}#article`,
    headline,
    description,
    url: pageUrl,
    datePublished,
    dateModified,
    inLanguage: 'en-GB',
    isAccessibleForFree: true,
    articleSection,
    wordCount,
    isPartOf: { '@id': WEBSITE_ID },
    mainEntityOfPage: { '@id': `${pageUrl}#webpage` },
    publisher: { '@id': ORGANIZATION_ID },
    author: { '@id': authorId },
    about: { '@id': SOFTWARE_ID },
  };
}

type HowToStep = { name: string; text: string; url?: string };

export function howToNode({
  pageUrl,
  name,
  description,
  steps,
  totalTime = 'PT10M',
}: {
  pageUrl: string;
  name: string;
  description: string;
  steps: readonly HowToStep[];
  totalTime?: string;
}) {
  return {
    '@type': 'HowTo',
    '@id': `${pageUrl}#howto`,
    name,
    description,
    totalTime,
    inLanguage: 'en-GB',
    image: {
      '@type': 'ImageObject',
      url: OG_IMAGE_URL,
      width: OG_IMAGE_WIDTH,
      height: OG_IMAGE_HEIGHT,
    },
    supply: { '@type': 'HowToSupply', name: 'Your social media accounts' },
    tool: { '@id': SOFTWARE_ID },
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.url ? { url: step.url } : {}),
    })),
  };
}

type DatasetVariable = { name: string; description: string; unitText?: string };

type DatasetOptions = {
  pageUrl: string;
  name: string;
  description: string;
  /** ISO date the figures were last recomputed. */
  dateModified: string;
  /** ISO date or interval the observations cover. */
  temporalCoverage?: string;
  variableMeasured: readonly DatasetVariable[];
  measurementTechnique: string;
  /** Licence URL governing reuse of the published figures. */
  license: string;
  /** The exact citation line the page asks readers to use. */
  citation: string;
  keywords: readonly string[];
  /** Version identifier, e.g. the ISO computation date. */
  version: string;
  /** Fetchable copies of the data, e.g. a JSON endpoint. */
  distribution?: readonly { encodingFormat: string; contentUrl: string }[];
};

/**
 * A first-party dataset published on a page. This is the schema type AI search
 * and Google Dataset Search look for when deciding whether a page carries
 * citable original data rather than restated commentary.
 */
export function datasetNode({
  pageUrl,
  name,
  description,
  dateModified,
  temporalCoverage,
  variableMeasured,
  measurementTechnique,
  license,
  citation,
  keywords,
  version,
  distribution,
}: DatasetOptions) {
  return {
    '@type': 'Dataset',
    '@id': `${pageUrl}#dataset`,
    name,
    description,
    url: pageUrl,
    inLanguage: 'en-GB',
    isAccessibleForFree: true,
    license,
    citation,
    keywords: [...keywords],
    version,
    creator: { '@id': ORGANIZATION_ID },
    publisher: { '@id': ORGANIZATION_ID },
    dateModified,
    temporalCoverage,
    measurementTechnique,
    variableMeasured: variableMeasured.map((variable) => ({
      '@type': 'PropertyValue',
      name: variable.name,
      description: variable.description,
      unitText: variable.unitText,
    })),
    distribution: distribution?.map((download) => ({ '@type': 'DataDownload', ...download })),
    includedInDataCatalog: {
      '@type': 'DataCatalog',
      '@id': `${pageUrl}#catalog`,
      name: 'OwlScran Creator Index',
      publisher: { '@id': ORGANIZATION_ID },
    },
  };
}

/**
 * A platform-specific variant of the product (Instagram / TikTok / YouTube).
 * Mints its own `@id` and links back to the canonical product.
 */
export function platformSoftwareNode({
  pageUrl,
  name,
  description,
  featureList,
}: {
  pageUrl: string;
  name: string;
  description: string;
  featureList: readonly string[];
}) {
  return {
    '@type': 'WebApplication',
    '@id': `${pageUrl}#software`,
    name,
    url: pageUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description,
    featureList: [...featureList],
    isPartOf: { '@id': SOFTWARE_ID },
    publisher: { '@id': ORGANIZATION_ID },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
    },
  };
}
